import t from 'react-translate';
import type { FileRejection } from 'react-dropzone';
import { MAX_NUMBER_OF_FILES } from '../constants';

export interface ErrorConsolidationResult {
  message: string;
  hasErrors: boolean;
}

/**
 * Consolidates multiple error messages into a single user-friendly message.
 * Removes duplicates and combines related errors when appropriate.
 *
 * @param errors - Array of error messages to consolidate
 * @returns Consolidated error result with message and hasErrors flag
 *
 * @example
 * consolidateErrors(['File too large', 'File too large', 'Invalid type'])
 * // Returns: { message: 'Multiple errors occurred...', hasErrors: true }
 */
export const consolidateErrors = (errors: string[]): ErrorConsolidationResult => {
  if (errors.length === 0) {
    return { message: '', hasErrors: false };
  }

  const uniqueErrors = Array.from(new Set(errors));

  if (uniqueErrors.length === 1) {
    return {
      message: uniqueErrors[0],
      hasErrors: true,
    };
  }

  const knownErrorMessages = [
    t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE(),
    t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.FILE_IS_TOO_LARGE(),
    t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.PAGES_LIMIT_EXCEEDED(),
  ];

  const matchedErrors = uniqueErrors.filter((error) => knownErrorMessages.some((knownError) => error.includes(knownError)));

  // Check if both FILE_IS_TOO_LARGE and PAGES_LIMIT_EXCEEDED errors are present
  const hasFileTooLargeError = uniqueErrors.some((error) => error.includes(t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.FILE_IS_TOO_LARGE()));
  const hasPageLimitError = uniqueErrors.some((error) => error.includes(t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.PAGES_LIMIT_EXCEEDED()));

  if (hasFileTooLargeError && hasPageLimitError) {
    return {
      message: t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.GENERAL_ERROR_MESSAGE(),
      hasErrors: true,
    };
  }

  if (matchedErrors.length > 1) {
    return {
      message: t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE(),
      hasErrors: true,
    };
  }

  return {
    message: uniqueErrors.join(' '),
    hasErrors: true,
  };
};

/**
 * Determines if an error should be shown immediately or queued for later.
 * Validation errors (file type, size, count) are shown immediately.
 * Upload errors are queued to be shown after all uploads complete.
 *
 * @param errorMessage - The error message to classify
 * @returns true if error should be shown immediately, false if it should be queued
 */
export const shouldShowErrorImmediately = (errorMessage: string): boolean => {
  const immediateErrors = [
    t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE(),
    t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.FILE_IS_TOO_LARGE(),
    t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE(),
  ];

  return immediateErrors.some((immediateError) => errorMessage.includes(immediateError));
};

/**
 * Helper to determine if an error is a file count limit error.
 * These should be shown immediately and prevent file addition.
 *
 * @param errorMessage - The error message to check
 * @returns true if it's a file count limit error
 */
export const isFileLimitError = (errorMessage: string): boolean => errorMessage.includes('limit') || errorMessage.includes('maximum');

/**
 * Extracts user-friendly error messages from react-dropzone FileRejection objects.
 * Maps dropzone error codes to translated error messages.
 * Handles special cases for multiple files with the same or different error types.
 *
 * @param fileRejections - Array of FileRejection objects from react-dropzone
 * @returns Array of translated error messages
 *
 * @example
 * extractFileRejectionErrors(rejections)
 * // Returns: ['File is too large', 'Invalid file type']
 */
export const extractFileRejectionErrors = (fileRejections: FileRejection[]): string[] => {
  if (fileRejections.length === 0) return [];

  const errorCodes = new Set<string>();

  fileRejections.forEach((rejection) => {
    rejection.errors.forEach((error) => {
      errorCodes.add(error.code);
    });
  });

  const hasDifferentErrors = errorCodes.size > 1;

  if (hasDifferentErrors) {
    return [
      t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE(),
    ];
  }

  const firstErrorCode = fileRejections[0]?.errors?.[0]?.code;

  if (firstErrorCode === 'file-too-large') {
    return [
      t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.FILE_IS_TOO_LARGE(),
    ];
  }

  if (firstErrorCode === 'file-invalid-type') {
    return [
      t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE(),
    ];
  }

  if (firstErrorCode === 'too-many-files') {
    return [t.LECTURE_PAGES.LHS.NOVO_AI.ERROR_MSG.FILES_LIMIT_ERROR(MAX_NUMBER_OF_FILES)];
  }

  return [];
};
